import * as React from "react";
import { Check, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

interface CircularButtonProps {
  isSelected: boolean;
  variant: "current" | "target";
  onClick: () => void;
  className?: string;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  showMinus?: boolean;
}

const CircularButton = React.forwardRef<HTMLButtonElement, CircularButtonProps>(
  ({ 
    isSelected, 
    variant, 
    onClick, 
    className, 
    disabled = false, 
    size = "md",
    showMinus = false,
    ...props 
  }, ref) => {
    const getSizeClasses = () => {
      switch (size) {
        case "sm":
          return "w-6 h-6";
        case "md":
          return "w-8 h-8";
        case "lg":
          return "w-10 h-10";
        default:
          return "w-8 h-8";
      }
    };

    const getVariantClasses = () => {
      if (variant === "current") {
        if (isSelected) {
          return "circular-button-current-selected text-white border-transparent hover:scale-105";
        }
        return "bg-transparent border-2 border-muted text-muted-foreground hover:border-muted-foreground/60 hover:scale-105 circular-button-shadow";
      } else {
        // target variant
        if (isSelected) {
          return "circular-button-target-selected text-white border-transparent hover:scale-105";
        }
        return "bg-transparent border-2 border-muted text-muted-foreground hover:border-muted-foreground/60 hover:scale-105 circular-button-shadow";
      }
    };

    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
          "rounded-full assessment-spring flex items-center justify-center",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
          "disabled:pointer-events-none disabled:opacity-50",
          "transform-gpu", // Better performance for animations
          getSizeClasses(),
          getVariantClasses(),
          className
        )}
        {...props}
      >
        {isSelected ? (
          showMinus ? (
            <Minus className="w-3 h-3" />
          ) : (
            <Check className="w-3 h-3" />
          )
        ) : null}
      </button>
    );
  }
);

CircularButton.displayName = "CircularButton";

export { CircularButton };